/* Timezones: how the full IANA set is presented and searched.
 *
 * The twin of `currencies.ts`. What is *supported* is every zone the runtime
 * knows, read from `Intl.supportedValuesOf('timeZone')` at runtime and never
 * from a constant here — `i18n.md`. What an Organisation *enables* is stored
 * per Organisation and is STORY-041's subject; this file only says what the
 * platform offers and how somebody finds a zone in it.
 */
/**
 * IANA *backward* pairs, ordered `[superseded, current]`.
 *
 * IANA renames cities and keeps the old identifier forever as a link. **Which
 * half of a pair a runtime LISTS is its own choice and changes between
 * versions**: this Node lists `Asia/Calcutta` and not `Asia/Kolkata`; a newer
 * build reverses it. Both accept either as input.
 *
 * So matching only the listed identifier fails for whichever name the runtime
 * did not pick, and that is not an exotic edge — it is Kolkata, Kyiv, Yangon,
 * Ho Chi Minh City, Nuuk. The zone's long name does not rescue it either: that
 * is *India Standard Time*.
 *
 * **This is an alias map, NOT the committed shortlist `i18n.md` forbids, and it
 * will be mistaken for one.** It does not decide what is supported — every zone
 * the runtime knows stays selectable whether or not it appears here. It only
 * lets one city be found by both of its names. Deleting it on sight, by
 * correctly applying the no-committed-lists rule to the wrong kind of list, is
 * the predictable mistake, so this paragraph lives next to the map itself.
 *
 * Measured against the well-known renames while prototyping STORY-041.
 */
export const ZONE_ALIASES = [
    ['Asia/Calcutta', 'Asia/Kolkata'],
    ['Asia/Rangoon', 'Asia/Yangon'],
    ['Asia/Saigon', 'Asia/Ho_Chi_Minh'],
    ['Asia/Katmandu', 'Asia/Kathmandu'],
    ['Asia/Dacca', 'Asia/Dhaka'],
    ['Asia/Thimbu', 'Asia/Thimphu'],
    ['Asia/Ujung_Pandang', 'Asia/Makassar'],
    ['Africa/Asmera', 'Africa/Asmara'],
    ['America/Godthab', 'America/Nuuk'],
    ['Europe/Kiev', 'Europe/Kyiv'],
    ['Pacific/Ponape', 'Pacific/Pohnpei'],
    ['Pacific/Truk', 'Pacific/Chuuk'],
    ['Atlantic/Faeroe', 'Atlantic/Faroe'],
    ['America/Buenos_Aires', 'America/Argentina/Buenos_Aires'],
];
/* Applied in BOTH directions, deliberately: the pair is symmetric and which
 * half the runtime lists is not ours to assume. */
const ALSO_KNOWN_AS = new Map();
for (const [superseded, current] of ZONE_ALIASES) {
    ALSO_KNOWN_AS.set(superseded, [...(ALSO_KNOWN_AS.get(superseded) ?? []), current]);
    ALSO_KNOWN_AS.set(current, [...(ALSO_KNOWN_AS.get(current) ?? []), superseded]);
}
/** The other spellings of the same place, whichever way round. */
export const zoneAliasesOf = (zone) => ALSO_KNOWN_AS.get(zone) ?? [];
const CURRENT_NAME = new Map(ZONE_ALIASES);
/**
 * The spelling of a zone in use today.
 *
 * **Display only.** The stored value is always the runtime's own identifier —
 * `i18n.md` is explicit that the map is presentation and never storage.
 */
export const zoneCurrentName = (zone) => CURRENT_NAME.get(zone) ?? zone;
/**
 * The city, from the identifier, under its current name.
 *
 * Somebody who searches "Kolkata" and gets back a row headed "Calcutta" is
 * right to wonder whether they found the wrong place. The identifier is still
 * shown in full beside it, so nothing is hidden; only the heading is brought up
 * to date.
 */
export const zoneCity = (zone) => (zoneCurrentName(zone).split('/').pop() ?? zone).replace(/_/g, ' ');
const offsets = new Map();
/**
 * The zone's offset **right now**, as `GMT+5:30`.
 *
 * **Display only, and it changes twice a year.** The IANA identifier is the
 * identity; this is never stored and never compared.
 *
 * It takes no instant, deliberately. It did until review on 2026-08-21, and the
 * parameter was a trap: the cache is keyed on the zone alone, so the first call
 * decided the answer and every later one ignored its own argument. `listTimezones`
 * primes all ~420 zones at screen load, so a caller asking for a January offset
 * in July was silently handed July's — wrong by an hour across a DST boundary,
 * with a test that asserted only "not empty" passing throughout.
 *
 * Nothing needs an offset at another instant. An Event's time is rendered in its
 * Venue's zone through `formatDate`, which is zone-aware and does its own
 * lookup; this is a label on a picker row. If a caller ever does need one, the
 * fix is a second function that does not cache, not a parameter this one honours
 * intermittently.
 */
export function zoneOffset(zone) {
    const cached = offsets.get(zone);
    if (cached !== undefined)
        return cached;
    let value = '';
    for (const timeZoneName of ['shortOffset', 'short']) {
        try {
            value =
                new Intl.DateTimeFormat('en', { timeZone: zone, timeZoneName })
                    .formatToParts(new Date())
                    .find((part) => part.type === 'timeZoneName')?.value ?? '';
            break;
        }
        catch {
            /* Older runtimes reject `shortOffset` and accept `short`. Falling through
             * rather than failing: an offset is decoration, and a zone with no offset
             * shown is still selectable. */
        }
    }
    offsets.set(zone, value);
    return value;
}
const longNames = new Map();
/**
 * The zone's localised long name — "India Standard Time".
 *
 * Cached, because formatting ~420 zones on every keystroke is measurably slow
 * and the value does not change while the process is alive.
 */
function zoneLongName(zone, locale) {
    const key = `${locale}\u0000${zone}`;
    const cached = longNames.get(key);
    if (cached !== undefined)
        return cached;
    let value = '';
    try {
        value =
            new Intl.DateTimeFormat(locale, { timeZone: zone, timeZoneName: 'long' })
                .formatToParts(new Date())
                .find((part) => part.type === 'timeZoneName')?.value ?? '';
    }
    catch {
        /* Unknown to this runtime — blank rather than throwing, for the same reason
         * as the offset above. */
    }
    longNames.set(key, value);
    return value;
}
/**
 * Every zone the runtime knows, collated by city for one locale.
 *
 * Sorted by city rather than by identifier: sorting by identifier orders them
 * by continent, which is not what anybody scanning the list is looking for.
 */
export function listTimezones(locale) {
    const collator = new Intl.Collator(locale);
    return Intl.supportedValuesOf('timeZone')
        .map((code) => ({ code, city: zoneCity(code), offset: zoneOffset(code) }))
        .sort((a, b) => collator.compare(a.city, b.city));
}
/**
 * The zones matching a query, by any of the five ways people arrive.
 *
 * The identifier (`Europe/Istanbul`), the city (`Istanbul`), the zone's long
 * name (`India Standard Time`), the offset (`+5:30` — how somebody who knows
 * only that their clock is half an hour off will look), and the city's OTHER
 * name where IANA renamed it.
 *
 * That last one is the whole reason `ZONE_ALIASES` exists, and the long name
 * does not cover it: `Asia/Calcutta` is *India Standard Time*, so somebody
 * typing "Kolkata" — the modern name of their own city — matches nothing
 * without it.
 *
 * **Which side is folded with which locale is the subtle part, and it differs
 * from `currencies.ts`.** A currency's NAME is localised — it comes from
 * `Intl.DisplayNames` in the reader's language — so it folds with the reader's
 * locale. A zone's city does not: it is cut out of the ASCII identifier, so
 * "Istanbul" is "Istanbul" for every reader. Folding it with a Turkish locale
 * produces the dotless `ıstanbul`, which the dotted needle a Turkish reader
 * types can never match — the language's own capital, unreachable by name, for
 * the readers most likely to want it. Only the long name folds with the locale.
 */
export function filterTimezones(options, query, locale) {
    const needle = query.trim().toLocaleLowerCase(locale);
    if (needle === '')
        return options;
    const ascii = fold(query.trim());
    const spaced = (value) => fold(value.replace(/_/g, ' '));
    return options.filter((option) => spaced(option.code).includes(ascii) ||
        fold(option.city).includes(ascii) ||
        zoneLongName(option.code, locale).toLocaleLowerCase(locale).includes(needle) ||
        fold(option.offset).includes(ascii) ||
        zoneAliasesOf(option.code).some((alias) => spaced(alias).includes(ascii)));
}
/**
 * Lowercased, decomposed, and stripped of combining marks.
 *
 * The stripping is what makes the dotted and dotless i meet. A Turkish reader
 * types `İstanbul`; folding that as ASCII leaves `i` plus a combining dot above,
 * which does not equal the plain `i` in the identifier. Decomposing and
 * dropping the mark lands both on `istanbul`. It earns its keep beyond Turkish
 * too — `Sao Paulo` finds `America/Sao_Paulo` typed either way.
 */
const fold = (value) => value
    .toLocaleLowerCase('en-US')
    .normalize('NFD')
    .replace(/\p{Mn}/gu, '');
//# sourceMappingURL=timezones.js.map